import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import {
  Briefcase, Lightbulb, MapPin, Globe, Star, WarningCircle, Target, PencilLine,
} from "@phosphor-icons/react";
import "katex/dist/katex.min.css";

/**
 * Encadrés pédagogiques : un titre (#, ##, ###…) dont l'intitulé correspond à l'un des types ci-dessous
 * (ex. « ## Exemple concret ») transforme le titre ET le texte qui le suit (jusqu'au prochain titre de
 * même niveau ou supérieur) en encadré coloré avec icône. Aucun balisage spécial à saisir pour l'enseignant.
 */
export const CALLOUTS = [
  { type: "situation", label: "Situation professionnelle", icon: Briefcase, test: /^(mise en )?situation( professionnelle)?\b|^contexte professionnel/,
    box: "border-blue-500/40 bg-blue-500/10", text: "text-blue-700 dark:text-blue-300" },
  { type: "exemple", label: "Exemple concret", icon: Lightbulb, test: /^exemples?( concrets?)?\b|^illustration\b|^cas pratique\b/,
    box: "border-emerald-500/40 bg-emerald-500/10", text: "text-emerald-700 dark:text-emerald-300" },
  { type: "focus", label: "Focus Côte d'Ivoire", icon: MapPin, test: /^focus\b|^zoom (sur )?(la )?cote d|^contexte ivoirien|^en cote d'?ivoire/,
    box: "border-orange-500/40 bg-orange-500/10", text: "text-orange-700 dark:text-orange-300" },
  { type: "international", label: "Ouverture internationale", icon: Globe, test: /^ouverture( internationale)?\b|^comparaison internationale|^regard international|^a l'international/,
    box: "border-violet-500/40 bg-violet-500/10", text: "text-violet-700 dark:text-violet-300" },
  { type: "retenir", label: "À retenir", icon: Star, test: /^a retenir\b|^points? cles?\b|^principe cle\b|^synthese\b|^en resume\b|^l'essentiel\b|^retenir\b/,
    box: "border-amber-500/50 bg-amber-500/10", text: "text-amber-700 dark:text-amber-300" },
  { type: "attention", label: "Attention", icon: WarningCircle, test: /^attention\b|^point de vigilance|^erreurs? (courantes?|a eviter)|^pieges?\b/,
    box: "border-red-500/40 bg-red-500/10", text: "text-red-700 dark:text-red-300" },
  { type: "objectifs", label: "Objectifs", icon: Target, test: /^objectifs?\b/,
    box: "border-teal-500/40 bg-teal-500/10", text: "text-teal-700 dark:text-teal-300" },
  { type: "activite", label: "À vous de jouer", icon: PencilLine, test: /^a vous de jouer|^activites?\b|^exercices?\b|^mise en pratique|^application\b|^travail demande/,
    box: "border-pink-500/40 bg-pink-500/10", text: "text-pink-700 dark:text-pink-300" },
];

const normalize = (s) =>
  s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/’/g, "'")
    .replace(/[*_`]/g, "").replace(/^[^a-z]+/, "").trim();

function matchCallout(title) {
  const n = normalize(title);
  return CALLOUTS.find((c) => c.test.test(n)) || null;
}

/** Découpe le Markdown en blocs { callout, title, text } (les blocs « normaux » ont callout = null). */
export function splitBlocks(md) {
  const blocks = [];
  let cur = { callout: null, title: "", level: 0, lines: [] };
  let inFence = false;
  const push = () => {
    if (cur.callout && !cur.lines.join("\n").trim()) {
      // encadré sans texte : on le rend comme un titre ordinaire
      cur = { callout: null, title: "", level: 0, lines: [`${"#".repeat(cur.level)} ${cur.title}`] };
    }
    if (cur.lines.length || cur.callout) blocks.push(cur);
  };
  for (const line of md.split("\n")) {
    if (/^\s*(```|~~~)/.test(line)) inFence = !inFence;
    const m = !inFence && line.match(/^(#{1,6})\s+(.*?)\s*#*\s*$/);
    if (m) {
      const level = m[1].length;
      const callout = matchCallout(m[2]);
      if (callout) {
        push();
        cur = { callout, title: m[2], level, lines: [] };
        continue;
      }
      if (cur.callout && level > cur.level) { cur.lines.push(line); continue; }   // sous-titre à l'intérieur de l'encadré
      push();
      cur = { callout: null, title: "", level: 0, lines: [line] };
      continue;
    }
    cur.lines.push(line);
  }
  push();
  return blocks;
}

// Les tableaux défilent dans leur propre cadre au lieu d'élargir la page (téléphone).
const mdComponents = {
  table: ({ node, ...props }) => (
    <div className="my-4 w-full overflow-x-auto"><table {...props} /></div>
  ),
};

const Md = ({ children }) => (
  <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]} components={mdComponents}>{children}</ReactMarkdown>
);

/**
 * Rendu en lecture seule d'un contenu de séquence au format Markdown,
 * avec support des tableaux/listes (GFM), des formules LaTeX (remark-math + KaTeX)
 * et des encadrés pédagogiques (situation, exemple, focus, à retenir…).
 */
export default function MarkdownContent({ content, className = "" }) {
  if (!content) return null;
  const blocks = splitBlocks(content);
  return (
    <div className={`prose prose-slate min-w-0 max-w-none break-words [overflow-wrap:anywhere] prose-headings:font-display prose-img:rounded-lg prose-a:text-primary ${className}`}>
      {blocks.map((b, i) => {
        if (!b.callout) return <Md key={i}>{b.lines.join("\n")}</Md>;
        const { icon: Icon, box, text } = b.callout;
        return (
          <aside key={i} className={`not-prose-margins my-5 rounded-xl border-l-4 border ${box} p-4`} data-testid={`callout-${b.callout.type}`}>
            <p role="heading" aria-level={3} className={`mb-2 flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wide ${text}`}>
              <Icon size={18} weight="duotone" /> {b.title.replace(/[*_`]/g, "")}
            </p>
            <div className="[&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
              <Md>{b.lines.join("\n")}</Md>
            </div>
          </aside>
        );
      })}
    </div>
  );
}
